import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { SectionTitle, Loading } from "@/components/common";
import { CheckCircle2, XCircle, Clock, Info, ShieldAlert, RefreshCw, Lock } from "lucide-react";
import { toast } from "sonner";

const TONE = {
  pass: { color: "#10B981", Icon: CheckCircle2, label: "PASS" },
  fail: { color: "#EF4444", Icon: XCircle, label: "FAIL" },
  pending: { color: "#F59E0B", Icon: Clock, label: "PENDING" },
  info: { color: "#3B82F6", Icon: Info, label: "INFO" },
};

export default function ProductionReadiness() {
  const [d, setD] = useState(null);
  const load = async () => { try { const { data } = await api.get("/square/production-readiness"); setD(data); } catch { setD(null); } };
  useEffect(() => { load(); }, []);
  if (!d) return <Loading label="Verifying Square readiness" />;

  return (
    <div className="max-w-4xl" data-testid="production-readiness">
      <SectionTitle sub="Read-only verification — this screen never enables Production">Square Production Readiness</SectionTitle>

      <div className="sg-card p-5 mb-5" style={{ borderColor: d.ready_for_production ? "#10B981" : "#F59E0B" }} data-testid="readiness-summary">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-head text-2xl font-bold" style={{ color: d.ready_for_production ? "#10B981" : "#F59E0B" }} data-testid="readiness-overall">
              {d.ready_for_production ? "READY FOR PRODUCTION" : "NOT READY — action required"}
            </div>
            <div className="text-sm mt-1" style={{ color: "var(--txt2)" }}>
              Environment: <b style={{ color: "var(--txt)" }}>{d.environment}</b> · Production enabled: <b style={{ color: d.production_enabled ? "#10B981" : "#EF4444" }}>{d.production_enabled ? "YES" : "NO"}</b>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-sm font-mono">
              <span style={{ color: "#10B981" }}>{d.pass} pass</span> · <span style={{ color: "#EF4444" }}>{d.fail} fail</span> · <span style={{ color: "#F59E0B" }}>{d.pending} pending</span>
            </div>
            <button className="sg-btn" style={{ minHeight: 40 }} onClick={load} data-testid="readiness-refresh"><RefreshCw size={15} /> Re-check</button>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        {d.checks.map((c) => {
          const t = TONE[c.status] || TONE.info;
          return (
            <div key={c.key} className="sg-card p-4 flex items-start gap-3" data-testid={`readiness-check-${c.key}`}>
              <t.Icon size={20} style={{ color: t.color, flexShrink: 0, marginTop: 2 }} />
              <div className="flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-head font-bold">{c.label}</span>
                  <span className="chip" style={{ fontSize: 10, color: t.color, borderColor: t.color }} data-testid={`readiness-status-${c.key}`}>{t.label}</span>
                </div>
                <div className="text-sm mt-1" style={{ color: "var(--txt2)" }}>{c.detail}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="sg-card p-5 mt-5 flex items-start gap-3" style={{ background: "rgba(245,158,11,0.08)" }} data-testid="readiness-safety-note">
        <ShieldAlert size={22} style={{ color: "#F59E0B", flexShrink: 0 }} />
        <div>
          <div className="font-head font-bold" style={{ color: "#F59E0B" }}>Owner safety rule</div>
          <div className="text-sm mt-1" style={{ color: "var(--txt2)" }}>{d.activation_note}</div>
          <button
            className="sg-btn mt-3"
            style={{ minHeight: 40, opacity: 0.6, cursor: "not-allowed" }}
            disabled
            onClick={() => toast.message("Production activation is a separate Owner-authorized action — not available from this screen.")}
            data-testid="activate-production-btn">
            <Lock size={14} /> Activate Production (requires separate Owner authorization)
          </button>
        </div>
      </div>
    </div>
  );
}
